import { initThemeFromSystem } from './utils/hooks/useThemeStore';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ProtectedRoute from './components/routing/ProtectedRoute/ProtectedRoute';
import { UserContext } from './utils/contexts/UserContext';

import BaseLayout from './components/Layouts/BaseLayout';
import SmashLoader from './components/Loader/SmashLoader';
import useAuthContextValue from './utils/hooks/useAuthContextValue';
import NotFound from './pages/NotFound/NotFound';

import Register from './pages/Authentication/Register';
import VerifyEmailToken from './pages/Authentication/VerifyEmailToken';
import VerifyEmail from './pages/Authentication/VerifyEmail';
import LoginPage from './pages/Authentication/Login';

import Profile from './pages/Profile/Profile';
import EditProfile from './pages/Profile/EditProfile/EditProfile';

import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ForgotPassword/ResetPassword';

import Interviews from './pages/Interview/Inteviews/Interviews';
import Interview from './pages/Interview/Interview/Interview';
import MyInterviewPage from './pages/Interview/MyQuestionspage';
import CreateInterview from './pages/Interview/CreateInterview';
import EditInterview from './pages/Interview/EditInterview';

import QuestionBank from './pages/Question/QuestionBank';
import CreateQuestionPage from './pages/Question/CreateQuestion';
import EditQuestionPage from './pages/Question/EditQuestion';

import Sessions from './pages/Session/Sessions';
import Session from './pages/Session/Session';
import CreateSession from './pages/Session/CreateSession';

import AIChatbot from './pages/AIChatbot/AIChatbot';

import Universities from './pages/Universities/Universities';
import University from './pages/Universities/University';
import UniversityEdit from './pages/Universities/UniversityEdit/UniversityEdit';
import UniversityInterviews from './pages/Universities/UniversityInterviews';

import {
  InterviewQuestionsTable,
  MultiSelectActionsTable,
  QuestionBankTable,
  ReadOnlyTable,
  StudentAssignmentTable,
} from './components/Interview/TableExamples';
import TableTest1 from './components/Testing/TableTest1';
import TimePicker from './components/forms/DateTimePicker/TimePicker';
import DnDTable from './components/Testing/QuestionTable';
import Opening from './components/AIChatbot/contents/Opening';

import { LayoutContext } from './utils/contexts/LayoutContext';
import { ToastContainer } from 'react-toastify';
import ChooseAgent from './components/Interview/ChooseAgent';
import StudentsResponses from './pages/StudentResponses/StudentsResponses';
import StudentResponse from './pages/StudentResponses/StudentResponse';
import PublicNavBar from './components/Navigation/PublicNavBar';
import LandingPage from './pages/Landing/LandingPage';
import ExpertReviewQueue from './pages/ExpertReviews/ExpertReviewQueue';
import ExpertReviewWorkspace from './pages/ExpertReviews/ExpertReviewWorkspace';
import ExpertManagement from './pages/Experts/ExpertManagement';
import ExpertInvitation from './pages/Authentication/ExpertInvitation';
import AccountInvitation from './pages/Authentication/AccountInvitation';
import StudentPortfolio from './pages/Portfolio/StudentPortfolio';
import StudentInterviews from './pages/Interview/StudentInterviews';
import AdminOrganizations from './pages/PlatformAdmin/AdminOrganizations';
import AiSpendLayout from './pages/PlatformAdmin/AiSpend/AiSpendLayout';
import AiSpendOverview from './pages/PlatformAdmin/AiSpend/AiSpendOverview';
import AiSpendTimeline from './pages/PlatformAdmin/AiSpend/AiSpendTimeline';
import AiSpendByFeature from './pages/PlatformAdmin/AiSpend/AiSpendByFeature';
import AiSpendByProvider from './pages/PlatformAdmin/AiSpend/AiSpendByProvider';
import AiSpendByModel from './pages/PlatformAdmin/AiSpend/AiSpendByModel';
import AiSpendByOrganization from './pages/PlatformAdmin/AiSpend/AiSpendByOrganization';
import AiSpendUsers from './pages/PlatformAdmin/AiSpend/AiSpendUsers';
import AiSpendUserDetail from './pages/PlatformAdmin/AiSpend/AiSpendUserDetail';
import AiSpendSessions from './pages/PlatformAdmin/AiSpend/AiSpendSessions';
import AiSpendErrors from './pages/PlatformAdmin/AiSpend/AiSpendErrors';
import AiSpendRuns from './pages/PlatformAdmin/AiSpend/AiSpendRuns';
import AiSpendDaily from './pages/PlatformAdmin/AiSpend/AiSpendDaily';
import AssignedPortfolios from './pages/ExpertReviews/AssignedPortfolios';
import OrganizationDashboard from './pages/OrganizationAdmin/OrganizationDashboard';
import Unauthorized from './pages/Unauthorized/Unauthorized';
import InterviewWorkspaceLayout from './pages/Interview/Workspace/InterviewWorkspaceLayout';
import InterviewOverview from './pages/Interview/Workspace/InterviewOverview';
import StudentInterviewPreview from './pages/Interview/Preview/StudentInterviewPreview';
import RouteErrorBoundary from './components/routing/RouteErrorBoundary/RouteErrorBoundary';
import PlatformAdminConsole from './pages/PlatformAdmin/PlatformAdminConsole';
import SkillManagement from './pages/PlatformAdmin/SkillManagement';
import IBSchoolsPage from './pages/IBSchools/IBSchoolsPage';
import IBPilotRequests from './pages/PlatformAdmin/IBPilotRequests';
import ProviderCredits from './pages/PlatformAdmin/ProviderCredits';
import UserGuide from './pages/UserGuide/UserGuide';

function LegacyOrganizationRedirect() {
  const { uuid } = useParams();
  return <Navigate to={`/organizations/${uuid}`} replace />;
}

function LegacyInterviewRedirect({ section = 'overview' }) {
  const { uuid } = useParams();
  return <Navigate to={`/organization/interviews/${uuid}/${section}`} replace />;
}

function App() {
  const [padding, setPadding] = useState('p-4');
  const authContextValue = useAuthContextValue();
  const { isLoading, isLoggedIn, isUniversity, isStudent, isExpert, isAdmin } = authContextValue;
  const landingPath = isStudent
    ? '/portfolio'
    : isExpert
      ? '/expert/reviews'
      : isUniversity
        ? '/organization/dashboard'
        : isAdmin
          ? '/platform'
          : '/profile';

  useEffect(() => {
    initThemeFromSystem();
  }, []);

  if (isLoading) return <SmashLoader />;

  return (
    <UserContext.Provider value={authContextValue}>
      <LayoutContext.Provider value={{ padding, setPadding }}>
        <ToastContainer
          position="top-center"
          theme={localStorage.getItem('theme')}
        />
        <Routes>
          <Route
            element={
              <ProtectedRoute
                isAllowed={!isLoggedIn}
                isLoading={isLoading}
                redirectPath={landingPath}
              >
                <PublicNavBar />
              </ProtectedRoute>
            }
          >
            <Route index element={<LandingPage />} />
            <Route path="/ib-schools" element={<IBSchoolsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/verify/" element={<VerifyEmail />} />
            <Route path="/auth/verify/:token" element={<VerifyEmailToken />} />
          </Route>

          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/expert-invite/:token" element={<ExpertInvitation />} />
          <Route path="/auth/account-invite/:token" element={<AccountInvitation />} />
          {/* <Route
            path="/auth/reset-password-verify/"
            element={<VerifyPassword />}
          /> */}
          <Route
            path="/auth/reset-password/:token"
            element={<ResetPassword />}
          />

          <Route
            element={
              <ProtectedRoute
                isAllowed={isLoggedIn}
                isLoading={isLoading}
                redirectPath="/login"
              >
                <RouteErrorBoundary><BaseLayout /></RouteErrorBoundary>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={landingPath} replace />} />
            <Route path="/profile" element={isUniversity ? <Navigate to="/organization/dashboard" replace /> : isAdmin ? <Navigate to="/platform" replace /> : <Profile />} />
            <Route path="/profile/edit" element={<EditProfile />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route
              element={
                <ProtectedRoute
                  isAllowed={isAdmin}
                  isLoading={isLoading}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="/platform" element={<PlatformAdminConsole />} />
              <Route path="/platform/skills" element={<SkillManagement />} />
              <Route path="/platform/ib-pilot-requests" element={<IBPilotRequests />} />
              <Route path="/platform/credits" element={<ProviderCredits />} />
            </Route>
            <Route path="/universities" element={isStudent ? <Navigate to="/organizations" replace /> : <Universities />} />
            <Route path="/universities/:uuid" element={isStudent ? <LegacyOrganizationRedirect /> : <University />} />
            <Route path="/portfolio/:studentUuid" element={<StudentPortfolio />} />
            <Route
              path="/universities/:uuid/interviews"
              element={<UniversityInterviews />}
            />
            <Route
              path="/interviews/session/:session_id"
              element={<AIChatbot />}
            />
            <Route
              element={
                <ProtectedRoute
                  isAllowed={isUniversity}
                  isLoading={isLoading}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="/profile/:uuid" element={<Profile />} />
              <Route path="/organization/dashboard" element={<OrganizationDashboard />} />
              <Route path="/organization/profile" element={<Navigate to="/organization/dashboard" replace />} />
              <Route path="/organization/interviews" element={<Interviews />} />
              <Route path="/organization/interviews/new" element={<CreateInterview embedded />} />
              <Route path="/organization/interviews/:uuid/preview" element={<StudentInterviewPreview />} />
              <Route path="/organization/interviews/:uuid" element={<InterviewWorkspaceLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<InterviewOverview />} />
                <Route path="questions" element={<Interview embedded />} />
                <Route path="questions/add" element={<MyInterviewPage embedded />} />
                <Route path="sessions" element={<Sessions embedded />} />
                <Route path="sessions/new" element={<CreateSession embedded />} />
                <Route path="reports" element={<StudentsResponses />} />
                <Route path="reports/:sessionUuid" element={<StudentResponse />} />
                <Route path="settings" element={<EditInterview embedded />} />
              </Route>
              <Route path="/organization/experts" element={<ExpertManagement />} />
              <Route path="/organization/reviews" element={<ExpertReviewQueue />} />
              <Route path="/organization/reviews/:uuid" element={<ExpertReviewWorkspace />} />
              <Route path="/organization/settings" element={<UniversityEdit organizationUuid={authContextValue.user?.university?.uuid} />} />
              <Route path="/universities/:uuid/edit" element={<UniversityEdit />} />
              <Route path="/experts" element={<Navigate to="/organization/experts" replace />} />

              <Route path="/interviews/:uuid" element={<LegacyInterviewRedirect />} />
              <Route
                path="/interviews/question-list/:uuid"
                element={<MyInterviewPage />}
              />
              <Route path="/interviews" element={<Interviews />} />
              <Route
                path="/interviews/create-interview"
                element={<Navigate to="/organization/interviews/new" replace />}
              />
              <Route
                path="/interviews/:uuid/edit"
                element={<LegacyInterviewRedirect section="settings" />}
              />

              <Route path="/interviews/:uuid/sessions">
                <Route index element={<Sessions />} />
                <Route path="create" element={<LegacyInterviewRedirect section="sessions/new" />} />
                <Route path=":sessionId" element={<Session />}>
                  {/* <Route path="start" element={<AIChatbot />} /> */}
                </Route>
              </Route>

              <Route path="/question/create" element={<CreateQuestionPage />} />
              <Route
                path="/question/question-bank"
                element={<QuestionBank />}
              />
              <Route
                path="/question/create-question"
                element={<CreateQuestionPage />}
              />
              <Route
                path="/question/edit-question/:uuid"
                element={<EditQuestionPage />}
              />

              <Route
                path="/interviews/:uuid/student-responses"
                element={<LegacyInterviewRedirect section="reports" />}
              />
              <Route
                path="/student-responses/:uuid"
                element={<StudentResponse />}
              />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  isAllowed={isExpert || isUniversity}
                  isLoading={isLoading}
                  redirectPath="/unauthorized"
                />
              }
            >
              <Route path="/expert/reviews" element={<ExpertReviewQueue />} />
              <Route path="/expert/reviews/:uuid" element={<ExpertReviewWorkspace />} />
              <Route path="/expert/portfolios" element={<AssignedPortfolios />} />
            </Route>

            <Route
              element={
                <ProtectedRoute isAllowed={isStudent} isLoading={isLoading} redirectPath="/unauthorized" />
              }
            >
              <Route path="/portfolio" element={<StudentPortfolio />} />
              <Route path="/organizations" element={<Universities />} />
              <Route path="/organizations/:uuid" element={<University />} />
              <Route path="/organizations/:uuid/interviews" element={<UniversityInterviews />} />
              <Route path="/my-interviews" element={<StudentInterviews />} />
            </Route>

            {/* Platform administration (role ADMIN) */}
            <Route
              element={
                <ProtectedRoute isAllowed={isAdmin} isLoading={isLoading} redirectPath="/unauthorized" />
              }
            >
              <Route path="/admin" element={<Navigate to="/admin/organizations" replace />} />
              <Route path="/admin/organizations" element={<AdminOrganizations />} />
              <Route path="/admin/ai-spend" element={<AiSpendLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<AiSpendOverview />} />
                <Route path="timeline" element={<AiSpendTimeline />} />
                <Route path="features" element={<AiSpendByFeature />} />
                <Route path="providers" element={<AiSpendByProvider />} />
                <Route path="models" element={<AiSpendByModel />} />
                <Route path="organizations" element={<AiSpendByOrganization />} />
                <Route path="users" element={<AiSpendUsers />} />
                <Route path="users/:subjectUuid" element={<AiSpendUserDetail />} />
                <Route path="sessions" element={<AiSpendSessions />} />
                <Route path="errors" element={<AiSpendErrors />} />
                <Route path="runs" element={<AiSpendRuns />} />
                <Route path="daily" element={<AiSpendDaily />} />
              </Route>
            </Route>
          </Route>

          {/* Public */}

          <Route path="/test">
            <Route path="1" element={<DnDTable />} />
            <Route path="2" element={<TableTest1 />} />
            <Route path="3" element={<SmashLoader />} />
            <Route path="4" element={<StudentAssignmentTable />} />
            <Route path="5" element={<QuestionBankTable />} />
            <Route path="6" element={<ReadOnlyTable />} />
            <Route
              path="7"
              element={
                <InterviewQuestionsTable interviewId="73ed9a46-7677-42c1-a019-6c530f3000a2" />
              }
            />
            <Route path="8" element={<MultiSelectActionsTable />} />
            <Route path="9" element={<TimePicker />} />
            <Route path="10" element={<Opening />} />
            <Route path="11" element={<ChooseAgent />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </LayoutContext.Provider>
    </UserContext.Provider>
  );
}

export default App;
